import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { authFetch } from "../utils/authFetch";
import { UserPlus, Users, Trash2, AlertCircle, X, Search, Globe, ChevronDown, Check } from "lucide-react";
import ShareLinksSection from "./ShareLinksSection";
import WhoCanOpen from "./WhoCanOpen";
import { audienceLabel, supportsLinkSharing, type ProjectAudience } from "./audience";

type ShareView = "members" | "share-links";

type Role = "admin" | "editor" | "viewer";
type OrgRole = "none" | Role;

interface SharingProject extends ProjectAudience {
  id: string;
  slug: string;
  internalRole?: OrgRole;
  isPublished?: boolean;
}

interface Props {
  project: SharingProject;
  /** Which sub-surface to render. Driven by the project-level tab so we
   * don't add a second tab row inside the page. */
  view: ShareView;
  onProjectUpdated?: (patch: Partial<SharingProject>) => void;
}

const ROLE_RANK: Record<OrgRole, number> = { none: 0, viewer: 1, editor: 2, admin: 3 };
const ALL_ROLES: Role[] = ["viewer", "editor", "admin"];

/** Per-user role options are restricted to roles strictly higher than the
 * org-wide grant — the explicit row exists only to elevate, never demote. */
function rolesAbove(orgRole: OrgRole): Role[] {
  return ALL_ROLES.filter(r => ROLE_RANK[r] > ROLE_RANK[orgRole]);
}

const ORG_ROLE_LABEL: Record<OrgRole, string> = {
  none: "No access",
  viewer: "Viewer",
  editor: "Editor",
  admin: "Admin",
};

const ORG_ROLE_DESC: Record<OrgRole, string> = {
  none: "Only people added below can access this project.",
  viewer: "Anyone at Joveo can view the published content.",
  editor: "Anyone at Joveo can edit content in this project.",
  admin: "Anyone at Joveo has full admin access.",
};

export default function ProjectSharingTab({ project, view, onProjectUpdated }: Props) {
  if (view === "share-links") {
    // A link is anonymous, so Private keeps nothing outside Joveo and Partner
    // is an exclusivity promise — create_share 409s both, and offering "New
    // link" there is a dead control.
    if (!supportsLinkSharing(project)) {
      const a = audienceLabel(project);
      return (
        <div className="px-5 py-8 text-center">
          <p className="text-[13px] text-slate-600 font-medium">Links are not available here</p>
          <p className="text-[11px] text-slate-400 mt-1 max-w-sm mx-auto">
            This is a <span className="font-medium">{a}</span> project. A share link is
            anonymous — anyone who has it can read, with no account — so it is only
            offered where the audience already allows that.
          </p>
        </div>
      );
    }
    return <ShareLinksSection projectSlug={project.slug} isPublished={project.isPublished ?? false} />;
  }
  return <PeopleView project={project} onProjectUpdated={onProjectUpdated} />;
}


function PeopleView({ project, onProjectUpdated }: { project: SharingProject; onProjectUpdated?: (patch: Partial<SharingProject>) => void; }) {
  const { user } = useAuth();
  const [orgRole, setOrgRole] = useState<OrgRole>(project.internalRole ?? "viewer");
  const [savingOrg, setSavingOrg] = useState(false);
  const [members, setMembers] = useState<Array<{ id: string; email: string; name: string | null; avatarUrl: string | null; role: Role; isSuperAdmin?: boolean }>>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  // Bumped after every change the server confirmed, so Who can open this
  // refetches then — not on the optimistic value, and not only when the
  // members payload happens to change (it leaves out viewer rows).
  const [saved, setSaved] = useState(0);
  const bump = () => setSaved(n => n + 1);

  // Available per-user roles depend on the org-wide grant.
  const availableRoles = useMemo(() => rolesAbove(orgRole), [orgRole]);
  const [addRole, setAddRole] = useState<Role>(availableRoles[0] ?? "admin");
  // Keep addRole in sync if the org level changes while the form is open.
  useEffect(() => {
    if (!availableRoles.includes(addRole)) {
      setAddRole(availableRoles[0] ?? "admin");
    }
  }, [availableRoles, addRole]);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`/api/admin/projects/${project.id}/members`);
    if (r.ok) setMembers(await r.json());
    setLoading(false);
  }, [project.id]);

  // Effect-driven load: aborts the previous request when project.id changes
  // so the right-side view doesn't show another project's members during
  // fast navigation. The imperative `load()` above is kept for post-mutation
  // refreshes (add/remove member); those are inherently fresh and can't race.
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const r = await authFetch(`/api/admin/projects/${project.id}/members`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) {
          const data = await r.json();
          if (ctrl.signal.aborted) return;
          setMembers(data);
        }
        setLoading(false);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [project.id]);

  // Sync the org dropdown with the prop when the parent refreshes.
  useEffect(() => { setOrgRole(project.internalRole ?? "viewer"); }, [project.internalRole]);

  const handleOrgRoleChange = async (next: OrgRole) => {
    if (next === orgRole) return;
    const prev = orgRole;
    setOrgRole(next);
    setSavingOrg(true);
    const r = await authFetch(`/api/admin/projects/${project.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ internal_role: next }),
    });
    setSavingOrg(false);
    if (!r.ok) {
      setOrgRole(prev);
      const d = await r.json().catch(() => ({}));
      setError(d.detail || "Failed to update sharing");
      return;
    }
    onProjectUpdated?.({ internalRole: next });
    bump();
  };

  const handleAdd = async () => {
    if (!addEmail.trim()) return;
    setError("");
    const r = await authFetch(`/api/admin/projects/${project.id}/members`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: addEmail.trim(), role: addRole }),
    });
    if (r.ok) {
      setAddEmail("");
      setShowAdd(false);
      load();
      bump();
    } else {
      const d = await r.json().catch(() => ({}));
      setError(d.detail || "Failed");
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Remove this member?")) return;
    await authFetch(`/api/admin/projects/${project.id}/members/${id}`, { method: "DELETE" });
    load();
    bump();
  };

  // Org-role dropdown open state.
  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  useEffect(() => {
    if (!orgPickerOpen) return;
    const close = () => setOrgPickerOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [orgPickerOpen]);

  // Hide rows that are now redundant with the org grant (their explicit role
  // is at-or-below the floor — they get the floor anyway). Also hide super
  // admins since their access is granted independently.
  const visibleMembers = useMemo(() => {
    const base = members
      .filter(m => !m.isSuperAdmin)
      .filter(m => ROLE_RANK[m.role] > ROLE_RANK[orgRole]);
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter(m => (m.name || "").toLowerCase().includes(q) || m.email.toLowerCase().includes(q));
  }, [members, search, orgRole]);

  return (
    <div className="space-y-5">
      {/* ── Org-wide grant ───────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <div className="size-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <Globe className="size-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-800">Anyone at Joveo</div>
            <div className="text-xs text-slate-500 mt-0.5">{ORG_ROLE_DESC[orgRole]}</div>
          </div>
          <div className="relative shrink-0" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setOrgPickerOpen(o => !o)}
              disabled={savingOrg}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 disabled:opacity-50 transition-colors"
            >
              <span>{ORG_ROLE_LABEL[orgRole]}</span>
              <ChevronDown className={`size-3.5 text-slate-400 transition-transform ${orgPickerOpen ? "rotate-180" : ""}`} />
            </button>
            {orgPickerOpen && (
              <div className="absolute right-0 top-full mt-1.5 z-30 w-56 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                {(["none", "viewer", "editor", "admin"] as OrgRole[]).map(r => {
                  const selected = r === orgRole;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => { handleOrgRoleChange(r); setOrgPickerOpen(false); }}
                      className={`w-full flex items-start gap-2 px-3 py-2 text-left transition-colors ${selected ? "bg-indigo-50/60" : "hover:bg-slate-50"}`}
                    >
                      <Check className={`size-3.5 mt-0.5 shrink-0 ${selected ? "text-indigo-600" : "text-transparent"}`} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs font-medium ${selected ? "text-indigo-700" : "text-slate-700"}`}>{ORG_ROLE_LABEL[r]}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5 leading-snug">{ORG_ROLE_DESC[r]}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Explicit per-user grants ─────────────────────────────────── */}
      <div className="space-y-3">
        {orgRole !== "admin" && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">
            Users ({visibleMembers.length})
          </span>
          {availableRoles.length > 0 && (
            <div className="relative">
              <button onClick={() => { setShowAdd(!showAdd); setError(""); setAddEmail(""); }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors">
                <UserPlus className="size-3.5" /> Add user
              </button>
              {showAdd && (
                <div className="absolute right-0 top-full mt-2 z-30 w-80 bg-white border border-slate-200 rounded-xl shadow-lg" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <span className="text-sm font-semibold text-slate-800">Add user</span>
                    <button onClick={() => setShowAdd(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100">
                      <X className="size-3.5" />
                    </button>
                  </div>
                  <div className="px-4 py-3 space-y-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Email</label>
                      <input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)}
                        placeholder="user@joveo.com" autoFocus
                        className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400" />
                    </div>
                    {availableRoles.length > 1 ? (
                      <div>
                        <label className="text-xs font-medium text-slate-600 mb-1.5 block">Role</label>
                        <div className="flex gap-1.5">
                          {availableRoles.map(r => (
                            <button key={r} type="button" onClick={() => setAddRole(r)}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors capitalize ${addRole === r ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"}`}>
                              {r}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-500">
                        Will be added as <span className="font-medium text-slate-700 capitalize">{availableRoles[0]}</span>.
                      </div>
                    )}
                    {error && <div className="flex items-center gap-2 text-xs text-red-600"><AlertCircle className="size-3.5" /> {error}</div>}
                    <div className="flex items-center gap-2 justify-end">
                      <button onClick={() => setShowAdd(false)} className="px-3 py-1.5 text-xs text-slate-500">Cancel</button>
                      <button onClick={handleAdd} disabled={!addEmail.trim()}
                        className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        )}

        {members.length > 5 && (
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 max-w-sm focus-within:ring-1 focus-within:ring-indigo-400 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
            <Search className="size-3.5 text-slate-400 shrink-0" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Filter by name or email…"
              className="flex-1 text-sm text-slate-700 placeholder:text-slate-400 bg-transparent outline-none" />
          </div>
        )}

        {loading ? <div className="text-sm text-slate-400 py-8 text-center">Loading…</div> : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            {visibleMembers.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Users className="size-6 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">
                  {orgRole === "admin"
                    ? "Everyone at Joveo already has admin access."
                    : `No one has elevated access. ${orgRole === "none" ? "Add users above." : `Anyone at Joveo gets ${ORG_ROLE_LABEL[orgRole]} access.`}`}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {visibleMembers.map(m => (
                  <div key={m.id} className="flex items-center justify-between px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      {m.avatarUrl
                        ? <img src={m.avatarUrl} className="size-7 rounded-full" alt="" />
                        : <div className="size-7 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-medium text-slate-500">{(m.name || m.email)[0].toUpperCase()}</div>}
                      <div>
                        <div className="text-sm font-medium text-slate-700">{m.name || m.email}</div>
                        {m.name && <div className="text-xs text-slate-400">{m.email}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {m.id !== user?.id ? (
                        <div className="flex items-center gap-0.5 bg-slate-100 rounded-md p-0.5">
                          {availableRoles.map(r => (
                            <button key={r} onClick={async () => {
                              if (r === m.role) return;
                              await authFetch(`/api/admin/projects/${project.id}/members`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ email: m.email, role: r }),
                              });
                              load();
                              bump();
                            }} className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors capitalize ${m.role === r ? "bg-white text-indigo-600 shadow-sm" : "text-slate-400 hover:text-slate-600"}`}>
                              {r}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${m.role === "admin" ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-600"}`}>
                          {m.role}
                        </span>
                      )}
                      {m.id !== user?.id && (
                        <button onClick={() => remove(m.id)} className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <WhoCanOpen projectId={project.id}
        refreshKey={`${saved}|${project.isPublished}`} />
    </div>
  );
}
